import { Alert, Box, Flex, Image, Link, ListItem, OrderedList, Stack, Table, TableCellProps, Tbody, Td, Text, Tr } from '@chakra-ui/react'
import {
  getDigitalSignatureInstructions,
  getDigitalSignatureSecondInstructions,
} from 'BrokerTsLib/src/dtos'
import {
  FormSection,
  FormSectionBody,
  FormSectionDescription,
  FormSectionHeader,
  FormSectionTitle,
} from '../../components/FormSection'
import { formatPrice, priceFormat } from 'BrokerTsLib/src/Utils/formatPrice'
import { logos } from '../../utils/companiesLogos'
import { useGetRiskVariants, useSelections } from '../../hooks'
import { ContractAcceptationType, Risk, SelectionType } from 'BrokerTsLib/src/enums'
import { useInsuranceParamsContext } from '../../layouts/Wizard'
import { InsuranceParamsPropertyDto, OfferPackage } from 'BrokerTsLib/src/dtos'
import { useGetInsurance } from '../../hooks/useInsuranceParams'
import { useContext } from 'react'
import { insurerOfferContext } from '../hoc/WithOffer'
import moment from 'moment'
import { Loading } from '../../components/Loading'
import { CancelContract } from '../CancelContract'
import QRCode from 'react-qr-code'
import { meetingRecordContext } from '../../pages/carkulka/wizard/5'

interface RecapTableRowProps extends TableCellProps {
  col1: string | React.ReactNode
  col2?: string | React.ReactNode
  col3?: string | React.ReactNode
  isHighlighted?: boolean
}

export const RecapTableRow = ({ col1, col2, col3, isHighlighted, sx, ...rest }: RecapTableRowProps) => {
  return (
    <Tr sx={{ m: 0, p: 0 }}>
      <Td sx={{ w: '15rem', p: 1, ...sx }}>
        {col1 && <Text sx={{ fontWeight: isHighlighted ? 'semibold' : 'medium' }}>{col1}</Text>}
      </Td>
      <Td sx={{ p: 1, ...sx }}>{col2 && col2}</Td>
      {col3 && (
        <Td sx={{ p: 1, fontWeight: isHighlighted ? 'semibold' : 'normal', textAlign: 'end', ...sx }}>
          {col3}
        </Td>
      )}
    </Tr>
  )
}

export const Recap = () => {
  const { data: dataPf } = useSelections(SelectionType.FrekvencePlatby)
  const pfEnum = dataPf?.data ?? []
  const { data: dataDp } = useSelections(SelectionType.ZpusobDigitalnihoPodpisu)
  const dpEnum = dataDp?.data ?? []

  const { insuranceParams: ip, refetchInsuranceParams } = useInsuranceParamsContext()
  const insuranceParams = ip as InsuranceParamsPropertyDto
  const { isLoading, offer: o } = useContext(insurerOfferContext)
  const offer = o as OfferPackage
  const cat = insuranceParams?.generalParams.contractAcceptationType
  const isContracted = !!insuranceParams?.generalParams.contractNumber
  const isCanceled = !!insuranceParams?.generalParams.contractCanceled

  const offerId = insuranceParams?.generalParams.selectedOfferId

  const {
    data: dataInsurance,
    refetch: refetchInsurance,
    isLoading: isLoadingInsurance,
  } = useGetInsurance(offerId!)
  const insurance = dataInsurance?.data ?? {}

  const { meetingRecord, isLoading: isMRLoading } =
    useContext(meetingRecordContext)

  const dpDescription = dpEnum
    .find((x) => x.id == (meetingRecord?.digitalSignatureType ?? 0))
    ?.description?.toLowerCase()
  let instructions: string[] = []
  let instructions2: string[] = []
  let signatureUrl = insurance?.digitalSignatureUrl
  let signatureSecondUrl = null
  if (offer && meetingRecord?.digitalSignatureType) {
    instructions = getDigitalSignatureInstructions(
      offer.insurerId,
      meetingRecord?.digitalSignatureType,
    )
    instructions2 = getDigitalSignatureSecondInstructions(
      offer.insurerId,
      meetingRecord?.digitalSignatureType,
    )

    if (
      instructions2.length > 0 &&
      signatureUrl &&
      signatureUrl?.indexOf('|') != -1
    ) {
      const urls = signatureUrl!.split('|')
      signatureUrl = urls[0]
      signatureSecondUrl = urls[1]
    }
  }

  const variantPart = insuranceParams.property.variants?.find(x => x.id == offer?.insuranceVariantId)?.variantParts?.find(x => x.risk == Risk.PROPERTY_TYPE)?.riskVariant
  const { data: dataPropertyTypes } = useGetRiskVariants(Risk.PROPERTY_TYPE)

  const complicityPart = offer?.parts.find(x => x.riskId == Risk.SPOL_PROPERTY)
  const complicityFloodPart = offer?.parts.find(x => x.riskId == Risk.SPOL_FLOOD)
  let responsibilityPart = offer?.parts.find(x => x.riskId == Risk.RESPONSIBILITY)
  if (responsibilityPart && responsibilityPart.limit == 0) { // někdy je kalkulován pouze limit odpovědnosti nemovitosti
    responsibilityPart = offer?.parts.find(x => x.riskId == Risk.RESPONSIBILITY_REALESTATE)
  }

  const complicityText = []
  if (complicityPart)
    complicityText.push(priceFormat(complicityPart.limit, null, '0 Kč'))

  if (complicityFloodPart)
    complicityText.push("povodeň " + priceFormat(complicityFloodPart.limit, null, '0 Kč'))

  return (
    <FormSection variant={isCanceled ? 'red' : 'default'}>
      <FormSectionHeader>
        <FormSectionTitle>Rekapitulace pojištění</FormSectionTitle>

        {offer &&
          <FormSectionDescription>
            <Image src={logos[offer.insurerId!]} alt={offer.insurerNameFull} width="64px" height="48px" />
          </FormSectionDescription>
        }
      </FormSectionHeader>

      {!offer && isLoading && <Loading large={true} />}
      {offer &&
        <FormSectionBody>
          <Table variant="unstyled">
            <Tbody>
              <RecapTableRow col1="Typ pojištění" col2={`${dataPropertyTypes?.data.find(x => x.id == variantPart)?.name} „${offer?.title}”`} />
              <RecapTableRow col1="Odpovědnost" col2={`${responsibilityPart?.limit ? priceFormat(responsibilityPart.limit, null, '0 Kč') : 'Bez pojištění'}`} />
              <RecapTableRow col1="Spoluúčast" col2={complicityText.join(', ')} />

              {offer.parts
                .filter(x => x.valid && x.included && !(x.packageItem?.isAddition ?? false))
                .map((part, index) =>
                  <RecapTableRow key={part.riskId}
                    col1={index === 0 ? "Rozsah pojištění" : ""}
                    col2={part.name}
                    col3={!!part.limit ? part.limit == 999999999 ? 'Neomezeně' : formatPrice({ price: part.limit }) : 'Ano'}
                  />)}

              <RecapTableRow
                isHighlighted
                col1="Celkem"
                col2={<Text sx={{ fontWeight: 'semibold' }}>{formatPrice({ price: offer.price })}</Text>}
              />
            </Tbody>
          </Table>

          <Table variant="unstyled">
            <Tbody>

              <RecapTableRow col1="Splátky pojistného" col2={pfEnum.find(x => x.id === offer.paymentFrequencyId)?.description} />
              <RecapTableRow col1="Splátka" col2={formatPrice({ price: offer.priceFrequency })} />
              <RecapTableRow col1="Počátek pojištění" col2={moment(new Date(insuranceParams?.generalParams.insuranceStartDate!)).format("D. M. YYYY")} />

              {isContracted &&
                <>
                  {/* <RecapTableRow col1="Způsob sjednání" col2={fsEnum.find(x => x.value === insuranceParams?.generalParams.contractAcceptationType?.toString())?.description} /> */}
                  <RecapTableRow col1="Datum sjednání" col2={moment(new Date(insuranceParams?.generalParams.contractCreatedDate!)).format("D. M. YYYY HH:mm")} />
                </>
              }

              {isContracted && cat === 2 &&
                <RecapTableRow col1="Splatnost pojistného"
                  col2={!!insuranceParams?.generalParams.paymentDateSpecial ?
                    insuranceParams.generalParams.paymentDateSpecial :
                    moment(new Date(insuranceParams?.generalParams.paymentDate!)).format("D. M. YYYY")} />
              }
              {isCanceled &&
                <RecapTableRow col1="Smlouva byla stornována" col2={`${moment(new Date(insuranceParams.generalParams.contractCanceledDate!)).format("D. M. YYYY HH:mm")}, ${insuranceParams.generalParams.contractCanceledBy}`} />
              }
            </Tbody>
          </Table>

          {isContracted && (cat === 2 || offer.infos) &&
            <Alert colorScheme="orange">
              <Stack direction='column'>
                {cat === 2 && <Text>Pokud nebude první pojistné uhrazeno v termínu, PS nevznikne!</Text>}

                {cat === ContractAcceptationType.DigitalSignature &&
                  dpDescription && (
                    <Text>
                      Doplňující informace k podpisu smlouvy - podpis{' '}
                      <Text as="span" sx={{ fontWeight: 600 }}>
                        {dpDescription}
                      </Text>
                    </Text>
                  )}

                {cat === ContractAcceptationType.DigitalSignature &&
                  !!signatureUrl && (
                    <>
                      <Text>
                        Pokud sjednáváte na zařízení s dotykovou obrazovkou
                        (mobil, tablet) můžete přímo přejít do
                        <Link
                          as={'a'}
                          target="_blank"
                          href={signatureUrl}
                          sx={{
                            color: 'primary.500',
                            fontWeight: 'medium',
                            paddingLeft: '5px',
                            textDecoration: 'underline',
                          }}
                        >
                          aplikace pro podepsání{' '}
                          {!!signatureSecondUrl
                            ? 'předsmluvního dokumentu'
                            : 'smlouvy'}
                        </Link>
                        .
                      </Text>
                      <Text sx={{ pb: 1 }}>
                        V opačném případě naskenujte QR kód a pokračujte dle
                        kroků níže.
                      </Text>
                      <Flex gap={4}>
                        <Box sx={{ maxWidth: '128px' }}>
                          <QRCode
                            value={signatureUrl}
                            viewBox={`0 0 256 256`}
                            level="M"
                            size={256}
                            style={{
                              height: 'auto',
                              maxWidth: '100%',
                              width: '100%',
                            }}
                          />
                        </Box>
                        <Box>
                          <OrderedList>
                            {instructions.map((instruction) => (
                              <ListItem key={instruction}>
                                {instruction}
                              </ListItem>
                            ))}
                          </OrderedList>
                        </Box>
                      </Flex>
                    </>
                  )}

                {cat === ContractAcceptationType.DigitalSignature &&
                  !!signatureSecondUrl && (
                    <>
                      <Text>
                        Dále na zařízení s dotykovou obrazovkou (mobil,
                        tablet) můžete přejděte do
                        <Link
                          as={'a'}
                          target="_blank"
                          href={signatureSecondUrl}
                          sx={{
                            color: 'primary.500',
                            fontWeight: 'medium',
                            paddingLeft: '5px',
                            textDecoration: 'underline',
                          }}
                        >
                          aplikace pro podepsání smlouvy
                        </Link>
                        .
                      </Text>
                      <Text sx={{ pb: 1 }}>
                        Nebo naskenujte QR kód a pokračujte dle kroků níže.
                      </Text>
                      <Flex gap={4}>
                        <Box sx={{ maxWidth: '128px' }}>
                          <QRCode
                            value={signatureSecondUrl}
                            viewBox={`0 0 256 256`}
                            level="M"
                            size={256}
                            style={{
                              height: 'auto',
                              maxWidth: '100%',
                              width: '100%',
                            }}
                          />
                        </Box>
                        <Box>
                          <OrderedList>
                            {instructions2.map((instruction) => (
                              <ListItem key={instruction}>
                                {instruction}
                              </ListItem>
                            ))}
                          </OrderedList>
                        </Box>
                      </Flex>
                    </>
                  )}

                {cat === ContractAcceptationType.DigitalSignature &&
                  !signatureUrl && (
                    <OrderedList>
                      {instructions.map((instruction) => (
                        <ListItem key={instruction}>{instruction}</ListItem>
                      ))}
                    </OrderedList>
                  )}

                {offer.infos && offer.infos.filter(x => x.message).map(x => (
                  <Text dangerouslySetInnerHTML={{ __html: x.message }}></Text>
                ))}
              </Stack>
            </Alert>
          }

          {insuranceParams?.generalParams.insuranceId &&
            <CancelContract
              callback={refetchInsuranceParams}
              isContracted={isContracted}
              contractCanceled={isCanceled}
              enableReactivation={true}
              insuranceId={insuranceParams?.generalParams.insuranceId}
            />
          }
        </FormSectionBody>
      }
    </FormSection>
  )
}
